import { useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export const CATEGORIES = [
  ["payment", "Pertanyaan Pembayaran", "Payment inquiry"], ["subscription", "Langganan / Pelan", "Subscription / Plan"],
  ["technical", "Sokongan Teknikal", "Technical support"], ["account", "Akaun & Log Masuk", "Account & login"],
  ["team", "Pasukan / Enterprise", "Team / Enterprise"], ["feedback", "Cadangan / Maklum Balas", "Suggestion / Feedback"], ["other", "Lain-lain", "Other"],
];

export const ContactForm = () => {
  const { L } = useApp();
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "", category: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.category) return setError(L("Sila pilih kategori.", "Please choose a category."));
    setBusy(true); setError("");
    try { setDone((await api.post("/public/contact", f)).data); window.scrollTo({ top: 0, behavior: "smooth" }); } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8" data-testid="contact-success">
      <CheckCircle2 className="text-emerald-600" size={32} />
      <h2 className="mt-4 text-xl font-bold">{L("Mesej dihantar!", "Message sent!")}</h2>
      <p className="mt-2 text-sm text-slate-600">{L("No. rujukan", "Reference no.")} <strong data-testid="contact-reference">#{done.reference}</strong>. {done.email_sent ? L("Emel pengesahan telah dihantar kepada anda. Kami akan membalas dalam 1–2 hari bekerja.", "A confirmation email has been sent to you. We'll reply within 1–2 working days.") : L("Kami akan membalas dalam 1–2 hari bekerja.", "We'll reply within 1–2 working days.")}</p>
      <Button variant="outline" className="mt-6 rounded-xl" onClick={() => { setDone(null); setF({ ...f, subject: "", message: "", category: "" }); }} data-testid="contact-new-btn">{L("Hantar mesej lain", "Send another message")}</Button>
    </div>
  );
  const field = (k, ms, en, props = {}) => (
    <div className="space-y-1.5"><Label htmlFor={`c-${k}`}>{L(ms, en)}</Label><Input id={`c-${k}`} value={f[k]} onChange={set(k)} className="h-11 rounded-xl" data-testid={`contact-${k}-input`} {...props} /></div>
  );
  return (
    <form onSubmit={submit} className="grid gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:grid-cols-2 sm:p-8" data-testid="contact-form">
      {field("name", "Nama", "Name", { required: true, minLength: 2, maxLength: 100 })}
      {field("email", "Emel", "Email", { required: true, type: "email" })}
      {field("phone", "No. telefon (pilihan)", "Phone (optional)", { type: "tel", maxLength: 30 })}
      <div className="space-y-1.5">
        <Label>{L("Kategori", "Category")}</Label>
        <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
          <SelectTrigger className="h-11 rounded-xl" data-testid="contact-category-select"><SelectValue placeholder={L("Pilih kategori", "Choose a category")} /></SelectTrigger>
          <SelectContent>{CATEGORIES.map(([k, ms, en]) => <SelectItem key={k} value={k} data-testid={`contact-category-${k}`}>{L(ms, en)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2">{field("subject", "Subjek", "Subject", { required: true, minLength: 3, maxLength: 150 })}</div>
      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="c-message">{L("Mesej", "Message")}</Label>
        <Textarea id="c-message" required minLength={10} maxLength={3000} rows={6} value={f.message} onChange={set("message")} className="rounded-xl" data-testid="contact-message-input" placeholder={L("Terangkan pertanyaan anda dengan terperinci…", "Describe your question in detail…")} />
      </div>
      {error && <p className="text-sm text-red-600 sm:col-span-2" data-testid="contact-error">{error}</p>}
      <Button type="submit" disabled={busy} className="h-12 rounded-xl bg-violet-600 hover:bg-violet-700 sm:col-span-2" data-testid="contact-submit-btn">
        {busy ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Send className="mr-2" size={16} />}{L("Hantar mesej", "Send message")}
      </Button>
    </form>
  );
};
